import type { ReactNode } from "react";

import { Button } from "../components/Button";
import { Badge } from "../components/Display";
import { Checkbox } from "../components/FormControls";
import { Icon } from "../components/Icon";
import { Input } from "../components/Input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/Overlays";
import { Spinner } from "../components/Spinner";
import { cn } from "../lib/cn";
import { acceptOf, type FileRule } from "../lib/files";
import { FileDropzone } from "./Forms";

// ── Progress ─────────────────────────────────────────────────────────

export function ProgressBar({
  value,
  label,
  tone = "brand",
  className,
}: {
  /** 0…1 */
  value: number;
  label: string;
  tone?: "brand" | "danger" | "success";
  className?: string;
}) {
  const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      className={cn("h-1.5 w-full overflow-hidden rounded-full bg-foreground/10", className)}
    >
      <div
        className={cn(
          "h-full rounded-full transition-[width]",
          tone === "danger" ? "bg-danger" : tone === "success" ? "bg-success" : "bg-brand",
        )}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

// ── Thumbnails ───────────────────────────────────────────────────────

export type MediaKind = "image" | "document" | "video";

export interface MediaTile {
  id: string;
  title: string;
  kind: MediaKind;
  /** Small rendition (or nothing yet: an icon is shown). */
  thumbnailUrl?: string | null;
  /** Shown as a badge when the file isn't ready (processing / failed) or is in the trash. */
  badge?: { label: string; tone: "warning" | "danger" | "info" | "neutral" } | null;
  /** One short line under the title, e.g. "1600 × 900 · 240 KB". */
  meta?: string;
}

const KIND_ICON = { image: "image", document: "file", video: "eye" } as const;

export function MediaThumb({
  item,
  className,
}: {
  item: Pick<MediaTile, "kind" | "thumbnailUrl" | "title">;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex aspect-square items-center justify-center overflow-hidden bg-surface-muted",
        className,
      )}
    >
      {item.thumbnailUrl ? (
        <img src={item.thumbnailUrl} alt="" loading="lazy" className="size-full object-cover" />
      ) : (
        <Icon name={KIND_ICON[item.kind]} size={28} className="text-muted-foreground" />
      )}
    </div>
  );
}

export interface MediaGridProps {
  items: MediaTile[];
  /** Click / Enter on a tile (e.g. open details). */
  onOpen?: (item: MediaTile) => void;
  /** Show a checkbox on each tile. */
  selectable?: boolean;
  selected?: string[];
  onToggle?: (item: MediaTile, selected: boolean) => void;
  labels: { select: (title: string) => string };
  className?: string;
}

/** Responsive grid of media tiles (library pages and the picker). */
export function MediaGrid({
  items,
  onOpen,
  selectable = false,
  selected = [],
  onToggle,
  labels,
  className,
}: MediaGridProps) {
  const chosen = new Set(selected);
  return (
    <ul
      className={cn(
        "grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6",
        className,
      )}
    >
      {items.map((item) => {
        const isSelected = chosen.has(item.id);
        return (
          <li
            key={item.id}
            className={cn(
              "group relative overflow-hidden rounded-card border bg-surface shadow-card transition-shadow hover:shadow-pop",
              isSelected ? "border-brand ring-2 ring-brand" : "border-border",
            )}
          >
            <button
              type="button"
              className="block w-full text-left focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none"
              onClick={() => (onOpen ? onOpen(item) : onToggle?.(item, !isSelected))}
            >
              <MediaThumb item={item} />
              <span className="block px-2.5 py-2">
                <span className="block truncate text-xs font-medium">{item.title}</span>
                {item.meta && (
                  <span className="block truncate text-[0.7rem] text-muted-foreground">
                    {item.meta}
                  </span>
                )}
              </span>
            </button>
            {item.badge && (
              <Badge tone={item.badge.tone} className="absolute top-2 left-2">
                {item.badge.label}
              </Badge>
            )}
            {selectable && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={(value) => onToggle?.(item, value === true)}
                aria-label={labels.select(item.title)}
                className="absolute top-2 right-2 bg-surface"
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function MediaPreview({
  item,
  imageUrl,
  className,
}: {
  item: Pick<MediaTile, "kind" | "title">;
  /** Larger rendition for images. */
  imageUrl?: string | null;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex aspect-video items-center justify-center overflow-hidden rounded-card bg-surface-muted",
        className,
      )}
    >
      {item.kind === "image" && imageUrl ? (
        <img src={imageUrl} alt={item.title} className="max-h-full max-w-full object-contain" />
      ) : (
        <Icon name={KIND_ICON[item.kind]} size={48} className="text-muted-foreground" />
      )}
    </div>
  );
}

// ── Uploads in progress ──────────────────────────────────────────────

export interface UploadItem {
  id: string;
  name: string;
  /** 0…1 */
  progress: number;
  status: "uploading" | "done" | "error";
  error?: string;
}

export function UploadList({
  uploads,
  labels,
}: {
  uploads: UploadItem[];
  labels: { uploading: string; done: string };
}) {
  if (uploads.length === 0) return null;
  return (
    <ul className="space-y-2" aria-live="polite">
      {uploads.map((upload) => (
        <li key={upload.id} className="rounded-lg border border-border bg-surface px-3 py-2">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="truncate font-medium">{upload.name}</span>
            <span
              className={cn(
                "shrink-0 text-xs",
                upload.status === "error" ? "text-danger" : "text-muted-foreground",
              )}
            >
              {upload.status === "error"
                ? upload.error
                : upload.status === "done"
                  ? labels.done
                  : `${Math.round(upload.progress * 100)}%`}
            </span>
          </div>
          <ProgressBar
            className="mt-1.5"
            value={upload.status === "error" ? 1 : upload.progress}
            tone={
              upload.status === "error" ? "danger" : upload.status === "done" ? "success" : "brand"
            }
            label={`${labels.uploading} ${upload.name}`}
          />
        </li>
      ))}
    </ul>
  );
}

// ── Picker ───────────────────────────────────────────────────────────

export interface MediaPickerLabels {
  title: string;
  description?: string;
  search: string;
  select: (title: string) => string;
  confirm: (count: number) => string;
  cancel: string;
  loadMore: string;
  empty: string;
  dropTitle: string;
  dropHint?: string;
  uploading: string;
  done: string;
  close: string;
}

export interface MediaPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: MediaTile[];
  loading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Allow choosing several files (e.g. a product gallery). */
  multiple?: boolean;
  selected: string[];
  onSelectedChange: (ids: string[]) => void;
  onConfirm: (ids: string[]) => void;
  /** Upload straight from the picker (omit to hide the drop zone). */
  onUpload?: (files: File[]) => void;
  uploads?: UploadItem[];
  /** What may be uploaded (`FILE_RULES.image`, …); `accept` alone only filters the file picker. */
  rule?: FileRule;
  accept?: string;
  labels: MediaPickerLabels;
  toolbar?: ReactNode;
}

/** Choose media from the library (data, search and uploads come from the caller). */
export function MediaPicker({
  open,
  onOpenChange,
  items,
  loading = false,
  search,
  onSearchChange,
  hasMore = false,
  onLoadMore,
  multiple = false,
  selected,
  onSelectedChange,
  onConfirm,
  onUpload,
  uploads = [],
  rule,
  accept,
  labels,
  toolbar,
}: MediaPickerProps) {
  const toggle = (item: MediaTile, on: boolean) => {
    if (!multiple) onSelectedChange(on ? [item.id] : []);
    else onSelectedChange(on ? [...selected, item.id] : selected.filter((id) => id !== item.id));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" closeLabel={labels.close}>
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          {labels.description && <DialogDescription>{labels.description}</DialogDescription>}
        </DialogHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="sm:w-80">
            <Input
              type="search"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={labels.search}
              aria-label={labels.search}
              startIcon={<Icon name="search" size={16} />}
            />
          </div>
          {toolbar}
        </div>
        {onUpload && (
          <FileDropzone
            onFiles={onUpload}
            rule={rule}
            accept={accept ?? (rule ? acceptOf(rule) : undefined)}
            multiple
            title={labels.dropTitle}
            hint={labels.dropHint}
          />
        )}
        <UploadList uploads={uploads} labels={labels} />
        {loading && items.length === 0 ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">{labels.empty}</p>
        ) : (
          <MediaGrid
            items={items}
            selectable
            selected={selected}
            onToggle={toggle}
            labels={{ select: labels.select }}
          />
        )}
        {hasMore && (
          <div className="flex justify-center">
            <Button variant="outline" loading={loading} onClick={onLoadMore}>
              {labels.loadMore}
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {labels.cancel}
          </Button>
          <Button disabled={selected.length === 0} onClick={() => onConfirm(selected)}>
            {labels.confirm(selected.length)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
